/**
 * Track 2: audio mixer settings (pure, no Web Audio) – master / music / sfx volume and mute,
 * persisted in localStorage. Every storage access is guarded: private windows or blocked storage must not break the game.
 */
export interface AudioSettings { master: number; music: number; sfx: number; muted: boolean }

export const DEFAULT_AUDIO_SETTINGS: AudioSettings = { master: 0.8, music: 0.5, sfx: 0.8, muted: false };
export const AUDIO_STORAGE_KEY = 'galaxis.audio.v1';

const clamp01 = (n: unknown, fallback: number): number =>
  typeof n === 'number' && Number.isFinite(n) ? Math.min(1, Math.max(0, n)) : fallback;

export function sanitizeSettings(raw: unknown): AudioSettings {
  const o = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const d = DEFAULT_AUDIO_SETTINGS;
  return { master: clamp01(o['master'], d.master), music: clamp01(o['music'], d.music), sfx: clamp01(o['sfx'], d.sfx), muted: o['muted'] === true };
}

function safeStorage(): Storage | null {
  try { return typeof localStorage === 'undefined' ? null : localStorage; } catch { return null; }
}

export function loadSettings(storage: Pick<Storage, 'getItem'> | null = safeStorage()): AudioSettings {
  try {
    const s = storage?.getItem(AUDIO_STORAGE_KEY);
    return s ? sanitizeSettings(JSON.parse(s)) : { ...DEFAULT_AUDIO_SETTINGS };
  } catch { return { ...DEFAULT_AUDIO_SETTINGS }; }
}

export function saveSettings(s: AudioSettings, storage: Pick<Storage, 'setItem'> | null = safeStorage()): void {
  try { storage?.setItem(AUDIO_STORAGE_KEY, JSON.stringify(s)); } catch { /* storage unavailable */ }
}

/** Bus gains actually sent to the Web Audio graph (squared curve: a slider feels linear to the ear). */
export function busGains(s: AudioSettings): { master: number; music: number; sfx: number } {
  const curve = (v: number) => v * v;
  return { master: s.muted ? 0 : curve(s.master), music: curve(s.music), sfx: curve(s.sfx) };
}

/** Music context → layer targets (0..1). Layers cross-fade; the base pad never fully drops while music plays. */
export type Mood = 'calm' | 'tension' | 'battle';
export const MOOD_LAYERS: Record<Mood, { calm: number; tension: number; battle: number }> = {
  calm:    { calm: 1,   tension: 0,   battle: 0 },
  tension: { calm: 0.5, tension: 1,   battle: 0 },
  battle:  { calm: 0.2, tension: 0.6, battle: 1 },
};

/**
 * Mood from game state: a live battle gives battle; hostile fleets next to or inside our systems give tension.
 * `hostileNear` is computed by the caller from data the client already has (no extra information is leaked).
 */
export function pickMood(input: { inBattle: boolean; hostileNear: boolean }): Mood {
  return input.inBattle ? 'battle' : input.hostileNear ? 'tension' : 'calm';
}
